/**
 * Browser sound effects using Web Audio API — no external audio files needed.
 * All sounds are generated programmatically.
 *
 * Each sound uses:
 *  - A fast linear attack (8ms) so there's no click on start
 *  - Exponential decay for a natural bell/chime tail
 *  - A harmonic overtone layer (triangle wave, lower volume) for warmth
 *
 * Uses a singleton AudioContext so it can be resumed after user interaction,
 * satisfying browsers' autoplay policy (Chrome, Firefox, Edge).
 */

let _ctx = null;

function getCtx() {
  try {
    if (!_ctx || _ctx.state === 'closed') {
      _ctx = new (window.AudioContext || window.webkitAudioContext)();
    }
    return _ctx;
  } catch {
    return null;
  }
}

/** Unlock the AudioContext on first user interaction (call once in app root or on any click). */
export function unlockAudio() {
  const ctx = getCtx();
  if (ctx && ctx.state === 'suspended') {
    ctx.resume().catch(() => {});
  }
}

async function resumedCtx() {
  const ctx = getCtx();
  if (!ctx) return null;
  if (ctx.state === 'suspended') {
    try { await ctx.resume(); } catch { return null; }
  }
  return ctx;
}

// Bell-quality tone: 8ms attack → exponential decay, sine wave.
function schedBell(ctx, freq, startAt, duration, volume = 0.28) {
  const osc  = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.type = 'sine';
  osc.frequency.setValueAtTime(freq, startAt);
  gain.gain.setValueAtTime(0, startAt);
  gain.gain.linearRampToValueAtTime(volume, startAt + 0.008);
  gain.gain.exponentialRampToValueAtTime(0.001, startAt + duration);
  osc.start(startAt);
  osc.stop(startAt + duration + 0.01);
}

// Soft harmonic overtone (triangle wave for warmth, decays faster than fundamental).
function schedOvertone(ctx, freq, startAt, duration, volume = 0.08) {
  const osc  = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.type = 'triangle';
  osc.frequency.setValueAtTime(freq, startAt);
  gain.gain.setValueAtTime(0, startAt);
  gain.gain.linearRampToValueAtTime(volume, startAt + 0.008);
  gain.gain.exponentialRampToValueAtTime(0.001, startAt + duration * 0.55);
  osc.start(startAt);
  osc.stop(startAt + duration * 0.55 + 0.01);
}

/**
 * Dual Ding-Dong — bold two-note doorbell: B5 (DING) then G5 (DONG).
 * Each note has a sharp bell attack + rich harmonics + long resonant tail.
 * The two notes overlap slightly so the DONG starts while DING still rings.
 */
export async function playNotifSound() {
  const ctx = await resumedCtx();
  if (!ctx) return;
  const t = ctx.currentTime;

  const playNote = (freq, startAt, vol) => {
    // Fundamental
    const o1 = ctx.createOscillator();
    const g1 = ctx.createGain();
    o1.connect(g1); g1.connect(ctx.destination);
    o1.type = 'sine';
    o1.frequency.setValueAtTime(freq, startAt);
    g1.gain.setValueAtTime(0, startAt);
    g1.gain.linearRampToValueAtTime(vol, startAt + 0.006);
    g1.gain.exponentialRampToValueAtTime(0.001, startAt + 0.80);
    o1.start(startAt); o1.stop(startAt + 0.82);

    // 2nd harmonic — octave above, decays faster
    const o2 = ctx.createOscillator();
    const g2 = ctx.createGain();
    o2.connect(g2); g2.connect(ctx.destination);
    o2.type = 'sine';
    o2.frequency.setValueAtTime(freq * 2, startAt);
    g2.gain.setValueAtTime(0, startAt);
    g2.gain.linearRampToValueAtTime(vol * 0.30, startAt + 0.006);
    g2.gain.exponentialRampToValueAtTime(0.001, startAt + 0.30);
    o2.start(startAt); o2.stop(startAt + 0.32);

    // 3rd harmonic — warm body (triangle), very short
    const o3 = ctx.createOscillator();
    const g3 = ctx.createGain();
    o3.connect(g3); g3.connect(ctx.destination);
    o3.type = 'triangle';
    o3.frequency.setValueAtTime(freq * 3, startAt);
    g3.gain.setValueAtTime(0, startAt);
    g3.gain.linearRampToValueAtTime(vol * 0.12, startAt + 0.006);
    g3.gain.exponentialRampToValueAtTime(0.001, startAt + 0.14);
    o3.start(startAt); o3.stop(startAt + 0.15);
  };

  playNote(988, t,        0.40);  // DING — B5 (bright, high)
  playNote(784, t + 0.30, 0.38);  // DONG — G5 (warm, lower), starts while DING still rings
}

/**
 * Chat message pop — quick ascending glide (440 → 700 Hz) with a small
 * bell accent on top. Similar feel to Slack / WhatsApp message sounds.
 */
export async function playChatSound() {
  const ctx = await resumedCtx();
  if (!ctx) return;
  const t = ctx.currentTime;
  const schedRingBurst = (startAt) => {
    schedBell(ctx, 880, startAt, 0.28, 0.26);
    schedOvertone(ctx, 1320, startAt, 0.22, 0.08);
    schedBell(ctx, 784, startAt + 0.16, 0.30, 0.24);
    schedOvertone(ctx, 1176, startAt + 0.16, 0.22, 0.07);
  };

  schedRingBurst(t);
  schedRingBurst(t + 0.55);
}

/**
 * Alert chime — ascending C major arpeggio (C5 → E5 → G5).
 * Clear and attention-grabbing but not harsh.
 */
export async function playAlertSound() {
  const ctx = await resumedCtx();
  if (!ctx) return;
  const t = ctx.currentTime;
  schedBell(ctx,     523, t,        0.40, 0.24);  // C5
  schedOvertone(ctx, 1047, t,       0.40, 0.07);  // C6 overtone
  schedBell(ctx,     659, t + 0.15, 0.40, 0.22);  // E5
  schedOvertone(ctx, 1319, t + 0.15, 0.40, 0.06); // E6 overtone
  schedBell(ctx,     784, t + 0.30, 0.50, 0.20);  // G5
  schedOvertone(ctx, 1568, t + 0.30, 0.50, 0.06); // G6 overtone
}

/**
 * Success confirmation — single clear E5 bell with a B5 fifth and E6 octave
 * layered in. Bright, immediate, and harmonious.
 */
export async function playSuccessSound() {
  const ctx = await resumedCtx();
  if (!ctx) return;
  const t = ctx.currentTime;
  schedBell(ctx,     659, t, 0.35, 0.22);  // E5 fundamental
  schedOvertone(ctx,  988, t, 0.35, 0.07); // B5 (perfect fifth)
  schedOvertone(ctx, 1319, t, 0.35, 0.05); // E6 (octave)
}
